import PlayerConfig from "../../components/PlayerConfig/PlayerConfigPanel";

export type Player = {
  name: string;
  score: number;
  joinTime: number;
  remainingSkips:number;
  categoryWeights?: Record<string, number>;
  config: PlayerConfig;
};

export interface PlayerConfig {
  [deckId: string]: number;
}

export interface TurnInfo {
  tsarId: string;
  tsarJoinTime:number;
  cues: Cue[];
  question: Question;
  timer: number;
  startTime: number;
  endTime?: number;
  submissions:  { [playerId: string]: Array<number> };
  submissionTimes?: { [playerId: string]: number };
  results: Results;
  roundLimit: number;
  turnMode?: TurnMode;
  skipped?: boolean;
  ftlData?: {
    streakId: string;
    streakIndex: number;
    optionCount: number;
    czarChoice: number;
    multiplier: number;
    submissions: { [playerId: string]: { choice: number; wasAlive: boolean; survived: boolean; points: number } };
  };
  herdData?: {
    streakId: string;
    streakIndex: number;
    optionCounts: number[];
    wasSplit: boolean;
    options: Cue[];
    submissions: { [playerId: string]: { choice: number | null; wasAlive: boolean; survived: boolean; points: number } };
  };
  rrData?: ReverseRoundResultsPayload;
  picassoData?: PicassoResultsPayload;
  versusGroupRanking?: number[];
  hasFriendTokens?: boolean;
}

export interface Question {
  id?: number;
  forTsar: string;
  aboutTsar: string;
}

export interface Cue {
  type: CueTypes;
  content: string;
  answerCardId?: number;
}

export enum CueTypes {
  text,
  image,
}

export interface Results {
  [playerId: string]: PlayerResultInfo;
}

export interface PlayerResultInfo {
  playerInfo: Player;
  playerTurnResults: PlayerTurnResults;
}
export interface PlayerTurnResults {
  sequence: Array<number>;
  swaps: Array<ScoredSwap>; //swap distance first, then swap indices
  points: number;
}

export interface ScoredSwap {
  score: number;
  swap: number[];
}

export interface PlayerEndResults {
  winnerName: string;
  winnerScore: number;
  worstPlayerName: string;
  worstPlayerAccuracy: number;
  bestPlayerName: string;
  bestPlayerAccuracy: number;
}

export interface Submissions {
  kek: number;
}

export interface GameConfig {
  roundTimer: number;
  roundLimit: number;

  maxSkips: number;
  allAboutMe: boolean;
  waitForHost: boolean;
  enableJoinDuringRound: boolean;
  enabledModes: string[];
  preset?: string;
  /** Which family of modes the host picked in the lobby (Versus vs Co-op). */
  entryMode?: "VERSUS" | "COOP";
  deckMode?: "topics" | "manual";
  selectedTopics?: string[];
  nsfwEnabled?: boolean;
  includeLowQuality?: boolean;
  topicLanguage?: string[];
}

// ─── FTL (Follow the Leader) Types ────────────────────────────────────────────

export enum TurnMode {
  STANDARD = "STANDARD",
  STANDARD_VERSUS = "STANDARD_VERSUS",
  FOLLOW_THE_LEADER = "FOLLOW_THE_LEADER",
  REVERSE_ROUND = "REVERSE_ROUND",
  RUN_WITH_THE_HERD = "RUN_WITH_THE_HERD",
  PICASSO = "PICASSO",
}

// ─── Standard-Versus (group-Borda) Types ──────────────────────────────────────

/** Broadcast when a STANDARD_VERSUS turn resolves: the shared group ranking + everyone's score. */
export interface StandardVersusResultsPayload {
  groupRanking: number[]; // cueNumbers, the aggregate "friend group" ranking
  cues: Cue[];
  question: Question;
  results: Results;
}

export interface FtlOption {
  index: number; // 1-based
  cue: Cue;
  color: string;
}

export interface FtlStreakState {
  streakId: string;
  czarId: string;
  streakIndex: number;
  alivePlayers: string[];
  deadPlayers: string[];
  options: FtlOption[];
  czarChoice: number | null;
  multiplier: number;
  question: Question;
  timer: number;
  startTime: number;
  totalNonCzar: number;
}

export interface FtlTurnInfo {
  options: FtlOption[];
  question: Question;
  timer: number;
  startTime: number;
  streakIndex: number;
  alivePlayers: string[];
  deadPlayers: string[];
  multiplier: number;
  czarId: string;
  streakId: string;
}

export interface FtlStreakTurnResult {
  streakIndex: number;
  czarChoice: number;
  multiplier: number;
  eliminations: string[];
  survivors: string[];
  pointsAwarded: { [playerId: string]: number };
}

export interface FtlStreakResults {
  streakId: string;
  czarId: string;
  turnResults: FtlStreakTurnResult[];
  totalPointsByPlayer: { [playerId: string]: number };
}


// ─── Reverse Round Types ──────────────────────────────────────────────────────

export interface ReverseRoundStartPayload {
  question: Question;
  cues: Cue[];
  timer: number;
  startTime: number;
  /** Present on resync (reconnect/reload) — whether this player already submitted stage 1 */
  hasSubmitted?: boolean;
}

export interface ReverseRoundGuessPhasePayload {
  shownSequence: number[];
  shownCues: Cue[];
  originPlayerId: string | null;
  isRandom: boolean;
  options: { id: string; name: string }[];
  optionCount: number;
  highRollAvailable: boolean;
  timer: number;
  startTime: number;
  /** Present on resync (reconnect/reload) — whether this player already guessed stage 2 */
  hasGuessed?: boolean;
}

export interface ReverseRoundSequenceCard {
  sequence: number[];
  submittedBy: string;
  submitterName: string;
  isRandom: boolean;
  shownTo: string[];
  guesses: {
    playerId: string;
    playerName: string;
    guessedId: string | null;
    guessedName: string | null;
    correct: boolean;
    points: number;
  }[];
}

export interface ReverseRoundResultsPayload {
  sequences: ReverseRoundSequenceCard[];
  playerPoints: { [playerId: string]: number };
  ownerBonuses: { [playerId: string]: number };
}

// ─── Run with the Herd Types ─────────────────────────────────────────────────

export interface HerdStreakState {
  streakId: string;
  streakIndex: number;
  alivePlayers: string[];
  deadPlayers: string[];
  options: Cue[];
  question: Question;
  timer: number;
  startTime: number;
}

export interface HerdTurnInfo {
  streakId: string;
  streakIndex: number;
  /** Present on every turn — a skip swaps the question mid-streak, and the header reads it here. */
  question?: Question;
  options: Cue[];
  alivePlayers: string[];
  deadPlayers: string[];
  timer: number;
  startTime: number;
}

export interface HerdTurnResult {
  streakIndex: number;
  optionCounts: number[];
  eliminations: string[];
  survivors: string[];
  pointsAwarded: { [playerId: string]: number };
  wasSplit: boolean;
  options: Cue[];
}

export interface HerdStreakResults {
  streakId: string;
  turnResults: HerdTurnResult[];
  totalPointsByPlayer: { [playerId: string]: number };
}

// ─── Picasso Types ──────────────────────────────────────────────────────────

export interface PicassoStartPayload {
  question: Question;
  timer: number;
  startTime: number;
}

export interface PicassoImposterMission {
  targetPlayerId: string;
  targetPlayerName: string;
}

export interface PicassoGuessPhasePayload {
  drawingA: string;
  drawingB: string;
  targetPlayerName: string;
  timer: number;
  startTime: number;
}

export interface PicassoChallengeResult {
  type: "imposter" | "archive" | "decoy";
  drawingA: string;
  drawingB: string;
  realLabel: "A" | "B";
  drawerAName: string;
  drawerBName: string;
  targetPlayerName: string;
  imposterName?: string;
  voters: {
    playerName: string;
    vote: "A" | "B" | null;
    correct: boolean | null;
    points: number;
  }[];
}

export interface PicassoResultsPayload {
  drawings: {
    playerId: string;
    playerName: string;
    drawingSvg: string;
    correctGuessCount: number;
    pointsEarned: number;
    isImposter?: boolean;
    imposterTarget?: string;
    pointBreakdown: string;
  }[];
  challengeResults: PicassoChallengeResult[];
  playerPoints: { [playerId: string]: number };
  imposterResults: {
    imposterName: string;
    targetName: string;
    accepted: boolean;
    fooledCount: number;
    imposterPoints: number;
  }[];
  archiveDuels?: {
    archiveSubmissionId: number;
    archiveDrawingSvg: string;
    pairedWithPlayerId: string;
    pairedWithPlayerName: string;
    sameQuestion: boolean;
    totalVoters: number;
    fooledCount: number;
  }[];
}

// ─── Creator Stats ───────────────────────────────────────────────────────────

export interface DeckStat {
  deckId: string;
  deckName: string;
  playCount: number;
}

export interface CreatorStats {
  dailyPlays: { day: string; count: number }[];
  totalPlays: number;
  uniquePlayers: number;
  globalRank: number;
  globalTotal: number;
  languageRanks: { language: string; rank: number; total: number }[];
  deckStats: DeckStat[];
}

// ─── Leaderboard & Public Profile ─────────────────────────────────────────────

export interface LeaderboardEntry {
  name: string;
  rank: number;
  playCount: number;
  deckCount: number;
}

export interface LeaderboardResponse {
  creators: LeaderboardEntry[];
  total: number;
}

export interface CreatorPublicProfile {
  name: string;
  bio: string;
  globalRank: number;
  globalTotal: number;
  decks: CardDeckPreview[];
}

// ─── Community / Announcement Types ──────────────────────────────────────────

export interface SocialLink {
  id: number;
  label: string;
  url: string;
  svgMarkup: string;
  enabled: boolean;
  sortOrder: number;
}

export interface CtaCard {
  id: number;
  icon: string;
  title: string;
  body: string;
  link: string;
  enabled: boolean;
  sortOrder: number;
}

export interface Announcement {
  id: number;
  title: string;
  body: string;
  active: boolean;
  priority: number;
  createdAt: string;
  updatedAt: string;
}

/** An admin message broadcast into running games at a round boundary (see IntermissionPanel). */
export interface Intermission {
  id: number;
  title: string;
  body: string;
  buttonLabel: string;
  active: boolean;
  priority: number;
  minPlayers: number;
  maxPlayers: number;
  /** CSV of 2-letter codes; empty means every language. */
  languages: string;
  everyRounds: number | null;
  afterMatches: number | null;
  oncePerLobby: boolean;
  hasImage: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Write shape. `image` is a base64 data URL; null clears the stored image, undefined leaves it. */
export type IntermissionInput = Omit<
  Intermission,
  "id" | "hasImage" | "createdAt" | "updatedAt"
> & { image?: string | null };

export interface CommunityStats {
  questionCount: number;
  answerCount: number;
  categoryCount: number;
  creatorCount: number;
  deckCount: number;
  drawingCount: number;
  activeLobbies: number;
}

export interface GalleryDrawing {
  id: number;
  drawingSvg: string;
  prompt: string;
  points: number;
  createdAt: string;
  likeCount: number;
  dislikeCount: number;
  reportCount: number;
  myVotes: string[];
}

export interface GalleryResponse {
  drawings: GalleryDrawing[];
  total: number;
  limit: number;
  offset: number;
}

/** Community gallery band: a question and the ids of its best drawings. */
export interface GalleryBand {
  questionCardId: number;
  prompt: string;
  language: string;
  drawingCount: number;
  drawingIds: number[];
}

export interface GalleryBandsPage {
  bands: GalleryBand[];
  total: number;
}

export interface EloHighlight {
  answerCardId: number;
  answer: string;
  type: string;
  elo: number;
  matchCount: number;
  wins: number;
  losses: number;
  winRate: number;
  categoryName: string;
  deckId: string;
}

export interface EloEntry {
  answerCardId: number;
  answer: string;
  type: string;
  elo: number;
  matchCount: number;
  wins: number;
  losses: number;
  rankEntropy: number;
  rankDistribution: number[];
  winRate: number;
}

export interface PcaData {
  categoryName: string;
  deckId: string;
  questionCardId: number;
  components: number[][];
  explainedVariance: number[];
  cardIds: number[];
  playerCount: number;
  cardCount: number;
  totalComparisons: number;
}

export interface QuestionInfo {
  questionCardId: number;
  forTsar: string;
  cardCount: number;
}

// ─── Analytics Insight Types ─────────────────────────────────────────────────

export interface InsightCardData {
  answerCardId: number;
  answer: string;
  elo: number;
  rankDistribution: number[];
  rankEntropy: number;
  matchCount: number;
}

export interface AnalyticsInsight {
  insightType: "divided" | "consensus" | "chaos" | "two_camps" | "predictable_chaos" | "hidden_consensus" | "context_shift";
  question: string;
  questionCardId: number;
  categoryName: string;
  deckId: string;
  deckName: string;
  playerCount: number;
  cards: InsightCardData[];
  highlightIndex: number;
  description: string;
}

export interface AnalyticsDeckEntry {
  deckId: string;
  deckName: string;
  deckLanguage: string;
  deckCreator: string;
  playCount: number;
  categoryCount: number;
  questionCount: number;
  totalCards: number;
}

export interface PopularCategory {
  categoryName: string;
  deckId: string;
  cardCount: number;
  deckName: string;
  deckLanguage: string;
}

export interface CardInfo {
  id: number;
  probability: number;
  categories: Array<string>;
}

export interface QuestionCardInfo extends CardInfo {

  forTsar: string;
  aboutTsar: string;
  picassoSuitable?: boolean;

}

export interface AnswerCardInfo extends CardInfo{
  answer: string;
  
}

export interface CardDeckJSON {
  deckInfo: CardDeckInfo,
  questionCards: QuestionCardInfo[],
  answerCards: AnswerCardInfo[],
}

export enum CardType {
  QUESTION,
  ANSWER,
}


export enum Role {
  CREATOR,
  MODERATOR,
  ADMIN
}

export interface CardDeckInfo {
  id: string;
  name: string;
  description: string;
  language: Language;
  featured: boolean;
}

export interface CardDeckPreview extends CardDeckInfo{
  questionCardCount: number,
  answerCardCount: number,
  categoriesCount:number,
  creatorName: string,
  playCount: number,
}

export interface Language {
  id:string;
  name:string;
}

export interface LanguagesType {
  [key:string]:Language
}

export enum ResultCardType {
  WINNER,
  BEST_ROUND,
  WORST_ROUND,
  PERFECT_ROUND,
  HARDEST_QUESTION,
  FULL_CONSENSUS,
  FULL_INVERSION,
  MIND_READER,
  PICKY,
  WE_DONT_TALK_ABOUT_IT,
  COGNITIVE_DISSONANCE,
  SIMP,
  DEADWEIGHT,
  CARRY,
  CIRCULAR_CHAIN,
  SOCIAL_GRAPH,
  COMEBACK,
  CONSISTENT,
  // Timing-based cards
  CONFIDENTLY_INCORRECT,
  SPEED_RUNNER,
  QUICK_TO_JUDGE,
  ONE_STEP_AHEAD,
  WEAKEST_LINK,
  BUCKLE,
  WE_NEED_TO_TALK,
  // FTL-specific cards
  FTL_SURVIVOR,
  FTL_FIRST_BLOOD,
  FTL_CZAR_SWEEP,
  FTL_IRON_WILL,
  // Reverse Round cards
  IMPOSTER_ALERT,
  THE_DETECTIVE,
  // Herd cards
  HERD_INSTINCT,
  LONE_WOLF,
  // Picasso cards
  PICASSO_SIGNATURE_STYLE,
  PICASSO_ART_FORGER,
  PICASSO_NOT_FOOLING_ANYONE,
  PICASSO_GALLERY_FAVORITE,
  PICASSO_ROUND_GALLERY,
  // Append only — must stay in lockstep with backend/src/resultCardTypes.ts, since these are
  // plain numeric enum values sent over the wire.
  COMMUNITY_ALIGNMENT,
}

export interface ResultCard {
  type: ResultCardType;
  title: string;
  subtitle?: string;
  body: string;
  data: Record<string, unknown>;
  priority: number;
}
